/**
 * 🍉 Fruit Bomb Game API Client for Zeta Admin
 * Specifications from ADMIN_GAMES_AND_EVENT_POINTS_README.md (Section 3.2)
 */

const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:18009/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  if (!token) {
    return { "Content-Type": "application/json" };
  }
  const authHeader =
    token.startsWith("Basic ") || token.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;
  return {
    "Content-Type": "application/json",
    Authorization: authHeader,
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message ||
          result?.error ||
          result?.sqlMessage ||
          result?.message ||
          result?.details)) ||
      (typeof result === "string" ? result : "API request failed");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * 1. Get Fruit Bomb Game Settings
 * Endpoint: GET /admin/game/fruit-Bomb/settings
 */
export const getFruitBombSettings = async () => {
  const baseUrl = getAdminBaseUrl();
  const endpoints = [
    `${baseUrl}/game/fruit-Bomb/settings`,
    `${baseUrl}/game/fruit_bomb/settings`,
    `${baseUrl}/game/fruit-bomb/settings`,
    `${baseUrl}/game/fruit_Bomb/settings`,
    `${baseUrl}/game/settings?game=fruit-bomb`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error fetching Fruit Bomb game settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * 2. Update Fruit Bomb Game Settings
 * Endpoint: PUT /admin/game/fruit-Bomb/settings
 * @param {Object} settingsData
 * @param {number} settingsData.scoreUnit
 * @param {number} settingsData.pointsPerUnit
 * @param {number} settingsData.maxPointsPerRound
 * @param {number} settingsData.maxScorePerRound
 * @param {number} settingsData.minPlaySeconds
 * @param {number} settingsData.cooldownSeconds
 * @param {boolean} settingsData.isActive
 */
export const updateFruitBombSettings = async (settingsData) => {
  const baseUrl = getAdminBaseUrl();
  const payload = {
    scoreUnit: Number(settingsData.scoreUnit ?? 100),
    pointsPerUnit: Number(settingsData.pointsPerUnit ?? 2),
    maxPointsPerRound: Number(settingsData.maxPointsPerRound ?? 1000),
    maxScorePerRound: Number(settingsData.maxScorePerRound ?? 200000),
    minPlaySeconds: Number(settingsData.minPlaySeconds ?? 10),
    cooldownSeconds: Number(settingsData.cooldownSeconds ?? 0),
    isActive: Boolean(settingsData.isActive),
  };

  const endpoints = [
    { url: `${baseUrl}/game/fruit-Bomb/settings`, method: "PUT" },
    { url: `${baseUrl}/game/fruit_bomb/settings`, method: "PUT" },
    { url: `${baseUrl}/game/fruit-bomb/settings`, method: "PUT" },
    { url: `${baseUrl}/game/fruit_Bomb/settings`, method: "PUT" },
    { url: `${baseUrl}/game/fruit-Bomb/settings`, method: "POST" },
  ];

  let lastResponse;
  for (const item of endpoints) {
    try {
      const response = await fetch(item.url, {
        method: item.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404 && response.status !== 405) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (item === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error updating Fruit Bomb game settings");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * 3. Get Fruit Bomb Game Audit History
 * Endpoint: GET /admin/game/fruit-Bomb/history?page=1&perPage=20&status=completed
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.perPage=20]
 * @param {string} [params.search=""]
 * @param {string} [params.status="all"]
 * @param {string} [params.eventId=""]
 */
export const getFruitBombHistory = async ({
  page = 1,
  perPage = 20,
  search = "",
  status = "all",
  eventId = "",
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();

  if (page) queryParams.append("page", String(page));
  if (perPage) {
    queryParams.append("perPage", String(perPage));
    queryParams.append("limit", String(perPage));
  }
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (status && status !== "all") queryParams.append("status", status.trim());
  if (eventId && eventId !== "all") queryParams.append("eventId", eventId.trim());

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const endpoints = [
    `${baseUrl}/game/fruit-Bomb/history${queryString}`,
    `${baseUrl}/game/fruit_bomb/history${queryString}`,
    `${baseUrl}/game/fruit-bomb/history${queryString}`,
    `${baseUrl}/game/fruit_Bomb/history${queryString}`,
    `${baseUrl}/game/history?game=fruit-bomb${queryString ? `&${queryParams.toString()}` : ""}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error fetching Fruit Bomb history");
      }
    }
  }

  return handleApiResponse(lastResponse);
};

/**
 * 4. Get Fruit Bomb Daily Trends
 * Endpoint: GET /admin/game/fruit-Bomb/trends?days=7
 * @param {Object} [params]
 * @param {number} [params.days=7] - 7, 14, or 30
 */
export const getFruitBombTrends = async ({ days = 7 } = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();
  queryParams.append("days", String(days || 7));

  const queryString = `?${queryParams.toString()}`;
  const endpoints = [
    `${baseUrl}/game/fruit-Bomb/trends${queryString}`,
    `${baseUrl}/game/fruit_bomb/trends${queryString}`,
    `${baseUrl}/game/fruit-bomb/trends${queryString}`,
    `${baseUrl}/game/fruit_Bomb/trends${queryString}`,
    `${baseUrl}/game/trends?game=fruit-bomb${queryString ? `&${queryParams.toString()}` : ""}`,
  ];

  let lastResponse;
  for (const url of endpoints) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        return handleApiResponse(response);
      }
      lastResponse = response;
      if (response.status !== 404) {
        return handleApiResponse(response);
      }
    } catch (netErr) {
      if (url === endpoints[endpoints.length - 1]) {
        throw new Error(netErr.message || "Network error fetching Fruit Bomb daily trends");
      }
    }
  }

  return handleApiResponse(lastResponse);
};
